import React, { useEffect, useState, useMemo } from 'react';
import {
  DEAL_STAGE,
  DEAL_STAGE_LABEL_VALUE_OBJ,
  Lead,
} from '@tassomutuo-backoffice/types';
import { Box, Menu, Typography } from '@mui/material';
import { CtaButton, TextInput, CheckboxInput } from '@tassomutuo-backoffice/ui';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { SetURLSearchParams } from 'react-router-dom';

interface IFormInputs {
  [DEAL_STAGE.CONTATTARE_CLIENTE]: boolean;
  [DEAL_STAGE.APPUNTAMENTO_DA_CONFERMARE]: boolean;
  [DEAL_STAGE.APPUNTAMENTO_CONFERMATO]: boolean;
  [DEAL_STAGE.APPUNTAMENTO_ANNULLATO_STB_15GG]: boolean;
  [DEAL_STAGE.IN_SVILUPPO]: boolean;
  [DEAL_STAGE.INCARICO_O_VOUCHER_OTTENUTO]: boolean;
  [DEAL_STAGE.INCARICO_VOUCHER_OTTENUTO]: boolean;
  [DEAL_STAGE.IN_ATTESA_DI_DELIBERA]: boolean;
  [DEAL_STAGE.IN_ATTESA_DELIBERA_VOUCHER]: boolean;
  [DEAL_STAGE.DELIBERATA]: boolean;
  [DEAL_STAGE.DELIBERATA_VOUCHER]: boolean;
  [DEAL_STAGE.ATTO_FISSATO]: boolean;
  [DEAL_STAGE.ATTO_NOTARILE_CONCLUSO]: boolean;
  [DEAL_STAGE.DROPPED]: boolean;
  [DEAL_STAGE.APPUNTAMENTO_DA_RIFISSARE]: boolean;
}

const formInitialState: IFormInputs = {
  [DEAL_STAGE.CONTATTARE_CLIENTE]: false,
  [DEAL_STAGE.APPUNTAMENTO_DA_CONFERMARE]: false,
  [DEAL_STAGE.APPUNTAMENTO_CONFERMATO]: false,
  [DEAL_STAGE.APPUNTAMENTO_ANNULLATO_STB_15GG]: false,
  [DEAL_STAGE.IN_SVILUPPO]: false,
  [DEAL_STAGE.INCARICO_O_VOUCHER_OTTENUTO]: false,
  [DEAL_STAGE.INCARICO_VOUCHER_OTTENUTO]: false,
  [DEAL_STAGE.IN_ATTESA_DI_DELIBERA]: false,
  [DEAL_STAGE.IN_ATTESA_DELIBERA_VOUCHER]: false,
  [DEAL_STAGE.DELIBERATA]: false,
  [DEAL_STAGE.DELIBERATA_VOUCHER]: false,
  [DEAL_STAGE.ATTO_FISSATO]: false,
  [DEAL_STAGE.ATTO_NOTARILE_CONCLUSO]: false,
  [DEAL_STAGE.DROPPED]: false,
  [DEAL_STAGE.APPUNTAMENTO_DA_RIFISSARE]: false,
};

interface IProps {
  showFilters: boolean;
  leads: Lead[];
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

const LeadFilterSection = ({ leads, searchParams, setSearchParams, showFilters=true }: IProps) => {
  const { handleSubmit, control, reset } = useForm<IFormInputs>({});

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Leggi i filtri dall'URL
  const statusFilter = searchParams.get('status');
  const searchFilter = searchParams.get('search') || '';

  // Calcola lo stato del form dai parametri URL
  const appliedFilter = useMemo((): IFormInputs => {
    if (!statusFilter) return formInitialState;

    const statuses = statusFilter.split(',');
    const newState = { ...formInitialState };

    statuses.forEach((status) => {
      if (status in newState) {
        newState[status as keyof IFormInputs] = true;
      }
    });

    return newState;
  }, [statusFilter]);

  // Conta i filtri applicati
  const appliedFilterCount = useMemo(() => {
    return Object.values(appliedFilter).filter((v) => !!v).length;
  }, [appliedFilter]);

  useEffect(() => {
    if (open) {
      reset(appliedFilter);
    }
  }, [open, appliedFilter, reset]);

  const onChangeText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const textToSearch = e.target.value;

    // Aggiorna i parametri URL mantenendo gli altri filtri
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      // Rimuovi action filter quando si cerca per testo
      newParams.delete('action');

      if (textToSearch) {
        newParams.set('search', textToSearch);
      } else {
        newParams.delete('search');
      }
      return newParams;
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openFilterMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onSubmitHandler: SubmitHandler<IFormInputs> = async (data) => {
    const statusToShow = Object.entries(data)
      .filter(([, value]) => value === true)
      .map(([key]) => key);

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      // Rimuovi action filter quando si applicano filtri status
      newParams.delete('action');

      if (statusToShow.length > 0) {
        newParams.set('status', statusToShow.join(','));
      } else {
        newParams.delete('status');
      }
      return newParams;
    });

    handleClose();
  };

  const resetFilter = () => {
    reset(formInitialState);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('status');
      return newParams;
    });
    handleClose();
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={2}
        px={4}
      >
        {
          showFilters ?
          <CtaButton
            variant={appliedFilterCount > 0 ? 'contained' : 'text'}
            onClick={openFilterMenu}
            sx={{ position: 'relative' }}
          >
            <FilterAltOutlinedIcon sx={{ mr: 1 }} fontSize="small" /> Filtra
            {appliedFilterCount > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -8,
                  bgcolor: (th) => th.palette.error.main,
                  p: 0.4,
                  borderRadius: '50%',
                  minHeight: 30,
                  minWidth: 30,
                }}
              >
                <Typography>{appliedFilterCount}</Typography>
              </Box>
            )}
          </CtaButton>
          : null
        }
        <Box sx={{ maxWidth: '300px' }}>
          <TextInput
            placeholder="Cerca per nome.."
            onChange={onChangeText}
            value={searchFilter}
          />
        </Box>
      </Box>
      {/*Filter menu*/}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          <Box sx={{ p: 2, minWidth: '300px' }}>
            <Typography fontWeight="bold">Filtra Lead</Typography>

            <Typography fontSize="14px" mt={2}>
              Stato lead
            </Typography>
            <Box display="flex" flexDirection="column">
              {Object.keys(DEAL_STAGE).map((k) => (
                <Controller
                  key={k}
                  control={control}
                  name={DEAL_STAGE[k as keyof typeof DEAL_STAGE]}
                  defaultValue={false}
                  render={({ field }) => (
                    <CheckboxInput
                      label={
                        DEAL_STAGE_LABEL_VALUE_OBJ[
                          k as keyof typeof DEAL_STAGE_LABEL_VALUE_OBJ
                        ].label
                      }
                      {...field}
                    />
                  )}
                />
              ))}
            </Box>

            <CtaButton
              variant="contained"
              sx={{ width: '100%', mt: 4 }}
              type="submit"
              loading={false}
            >
              APPLICA FILTRI
            </CtaButton>

            {appliedFilterCount ? (
              <CtaButton
                sx={{ width: '100%', mt: 1 }}
                onClick={resetFilter}
                loading={false}
              >
                RIMUOVI FILTRI
              </CtaButton>
            ) : null}
          </Box>
        </form>
      </Menu>
    </>
  );
};

export default LeadFilterSection;
